import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, ShoppingCart, Package } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { formatPrice } from '../utils/formatPrice';

// Helper to add to cart
const addToCart = (product, category = 'general') => {
  const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
  const existingIndex = cart.findIndex(item => item.slug === product.slug);
  
  if (existingIndex >= 0) {
    cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + 1;
  } else {
    cart.push({
      slug: product.slug,
      name: product.name,
      price: product.price_ttc,
      image: product.images?.[0] || '',
      quantity: 1,
      category: category
    });
  }
  
  localStorage.setItem('spa_cart', JSON.stringify(cart));
  window.dispatchEvent(new Event('cartUpdated'));
  
};

// Normalize text for search (remove accents, lowercase)
const normalizeText = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .trim();
};

// Search scoring function
const getSearchScore = (product, searchTerms) => {
  let score = 0;
  const name = normalizeText(product.name);
  const brand = normalizeText(product.brand);
  const sku = normalizeText(product.sku);
  const model = normalizeText(product.model);
  const subCategory = normalizeText(product.sub_category);
  const shortDesc = normalizeText(product.short_description);
  
  for (const term of searchTerms) {
    // Exact matches in SKU/model get highest score
    if (sku === term || model === term) {
      score += 100;
    } else if (sku?.includes(term) || model?.includes(term)) {
      score += 50;
    }
    
    // Name matches
    if (name?.includes(term)) {
      score += 30;
      // Bonus for word start
      if (name.startsWith(term) || name.includes(` ${term}`)) {
        score += 20;
      }
    }
    
    // Brand matches
    if (brand?.includes(term)) {
      score += 25;
    }
    
    // Category matches
    if (subCategory?.includes(term)) {
      score += 15;
    }
    
    // Description matches
    if (shortDesc?.includes(term)) {
      score += 5;
    }
  }
  
  return score;
};

/**
 * Unified Catalog Search Bar Component
 * Used across all catalog pages for consistent UX
 * 
 * @param {Array} products - List of products to search through
 * @param {Function} onSearchResults - Callback when search results change (null = cleared)
 * @param {string} placeholder - Placeholder text
 * @param {string} category - Category name for cart tracking
 * @param {string} detailUrlPrefix - URL prefix for product detail links
 * @param {React.Component} emptyIcon - Icon to show when no image
 * @param {boolean} showHelpText - Show help text below search bar
 */
const CatalogSearchBar = ({ 
  products = [], 
  onSearchResults = null,
  placeholder = "Rechercher une pièce ou une référence (ex : pompe LX WP250, clavier Balboa, filtre PRB50)",
  category = "general",
  detailUrlPrefix = "/pieces-accessoires",
  emptyIcon: EmptyIcon = Package,
  showHelpText = true
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const performSearch = useCallback((query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      if (onSearchResults) onSearchResults(null);
      return;
    }

    setIsSearching(true);
    
    // Small delay for UI feedback
    setTimeout(() => {
      const searchTerms = normalizeText(query).split(/\s+/).filter(t => t.length >= 2);
      
      let filtered = [...products];
      
      // Apply text search
      if (searchTerms.length > 0) {
        filtered = filtered
          .map(product => ({
            product,
            score: getSearchScore(product, searchTerms)
          }))
          .filter(item => item.score > 0)
          .sort((a, b) => b.score - a.score)
          .map(item => item.product);
      }
      
      setSearchResults(filtered.slice(0, 8)); // Show max 8 results in dropdown
      setShowDropdown(true);
      setIsSearching(false);
      
      // Callback for parent component
      if (onSearchResults) {
        onSearchResults(query.trim() ? filtered : null);
      }
    }, 100);
  }, [products, onSearchResults]);

  // Handle search input change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 200);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    if (onSearchResults) onSearchResults(null);
  };

  return (
    <div className="w-full max-w-4xl mx-auto mb-8" data-testid="catalog-search-bar">
      {/* Search Container - Enhanced visibility */}
      <div className="relative">
        <div className="bg-white rounded-xl border-2 border-slate-200 shadow-md overflow-hidden hover:border-[#1E3A5F]/50 focus-within:border-[#1E3A5F] focus-within:ring-4 focus-within:ring-[#1E3A5F]/10 transition-all">
          <div className="flex items-center">
            {/* Search Icon - More visible */}
            <div className="pl-5 flex items-center">
              <Search className={`w-6 h-6 ${isSearching ? 'text-[#F97316] animate-pulse' : 'text-[#1E3A5F]'}`} />
            </div>
            
            {/* Search Input - Larger */}
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchQuery && setShowDropdown(true)}
              placeholder={placeholder}
              className="flex-1 px-4 py-4 text-slate-900 placeholder-slate-400 focus:outline-none text-base md:text-lg"
              data-testid="catalog-search-input"
            />
            
            {/* Clear Button */}
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="p-2 mr-4 text-slate-400 hover:text-slate-600 transition-colors rounded-full hover:bg-slate-100"
                data-testid="catalog-search-clear"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
        
        {/* Help Text below search bar */}
        {showHelpText && !searchQuery && (
          <p className="text-center text-sm text-slate-500 mt-3">
            Vous pouvez rechercher directement une référence de pièce, une marque ou un type de produit.
          </p>
        )}
        
        {/* Search Results Dropdown */}
        {showDropdown && searchQuery && (
          <div 
            ref={dropdownRef}
            className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-40"
            data-testid="catalog-search-dropdown"
          >
            {searchResults.length > 0 ? (
              <>
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
                  <span className="text-xs text-slate-500 font-medium">
                    {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} trouvé{searchResults.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                  {searchResults.map((product) => (
                    <div 
                      key={product.slug}
                      className="flex items-center gap-4 p-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                    >
                      {/* Product Image */}
                      <Link 
                        to={`${detailUrlPrefix}/${product.slug}`}
                        onClick={() => setShowDropdown(false)}
                        className="flex-shrink-0"
                      >
                        <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                          {product.images?.[0] ? (
                            <img 
                              src={product.images[0]} 
                              alt={product.name}
                              className="w-full h-full object-contain p-1"
                            />
                          ) : (
                            <EmptyIcon className="w-6 h-6 text-slate-300" />
                          )}
                        </div>
                      </Link>
                      
                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={`${detailUrlPrefix}/${product.slug}`}
                          onClick={() => setShowDropdown(false)}
                        >
                          <p className="text-xs text-slate-500 uppercase tracking-wide">{product.brand}</p>
                          <h4 className="font-medium text-slate-900 text-sm truncate hover:text-[#1E3A5F]">
                            {product.name}
                          </h4>
                          {product.sku && (
                            <p className="text-xs text-slate-400">Réf: {product.sku}</p>
                          )}
                        </Link>
                      </div>
                      
                      {/* Price & Cart */}
                      <div className="flex-shrink-0 text-right">
                        {product.price_ttc ? (
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-900 text-sm">{formatPrice(product.price_ttc)}</span>
                            <Button 
                              size="sm" 
                              onClick={(e) => {
                                e.preventDefault();
                                addToCart(product, category);
                              }}
                              className="bg-[#1E3A5F] hover:bg-[#152C4A] h-8 w-8 p-0"
                            >
                              <ShoppingCart className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-500">Sur devis</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="p-6 text-center">
                <EmptyIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium mb-1">Aucun produit trouvé</p>
                <p className="text-sm text-slate-500 mb-4">
                  Essayez avec une autre référence ou mot-clé
                </p>
                <Link 
                  to="/contact" 
                  onClick={() => setShowDropdown(false)}
                  className="text-sm text-[#1E3A5F] font-medium hover:underline"
                >
                  Nous contacter pour vérifier une référence
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Search Results Count (outside dropdown) */}
      {searchQuery && !showDropdown && (
        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-slate-600">
            <span className="font-semibold">{searchResults.length}</span> résultat{searchResults.length !== 1 ? 's' : ''} pour "{searchQuery}"
          </p>
          <button 
            onClick={clearSearch}
            className="text-sm text-[#1E3A5F] font-medium hover:underline"
          >
            Effacer
          </button>
        </div>
      )}
    </div>
  );
};

export default CatalogSearchBar;
